import React, { useMemo, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

// Renders the commission streamlining task force decisions CSV grouped by meeting date/topic.
// Empty "Date & Topic" cells inherit the last non-empty value.
// CSV columns: Date & Topic, Name, Decision
export default function StreamliningDecisions({ showDecisionLegend = true }) {
  const data = useStaticQuery(graphql`
    query StreamliningDecisionsQuery {
      allStreamliningDecisionsCsv {
        nodes {
          Date___Topic
          Name
          Decision
          notes: Notes
          url: Url
          description: Description
        }
      }
      allAllCommitteeDecisionsCsv {
        nodes {
          name
          decision
          comments
          date
        }
      }
    }
  `);

  // Gatsby's CSV transformer converts headers with spaces & special chars to underscores.
  // "Date & Topic" becomes Date___Topic (ampersand & space => ___).
  const rows = data?.allStreamliningDecisionsCsv?.nodes || [];
  const allCommitteeRows = data?.allAllCommitteeDecisionsCsv?.nodes || [];

  const nameToDecisions = useMemo(() => {
    const map = new Map();
    allCommitteeRows.forEach((r) => {
      const key = (r.name || '').trim().toLowerCase();
      if (!key) return;
      const list = map.get(key) || [];
      list.push({
        decision: r.decision || '',
        comments: r.comments || '',
        date: r.date || '',
      });
      map.set(key, list);
    });
    return map;
  }, [allCommitteeRows]);

  const groups = useMemo(() => {
    const out = [];
    let currentKey = null;
    let currentItems = [];
    let lastTopic = null;
    rows.forEach((r) => {
      const rawTopic = r.Date___Topic || '';
      const topic = rawTopic && rawTopic.trim() !== '' ? rawTopic.trim() : lastTopic;
      if (topic !== currentKey) {
        if (currentItems.length) out.push({ topic: currentKey, items: currentItems });
        currentKey = topic;
        currentItems = [];
      }
      lastTopic = topic;
      const itemName = r.Name;
      const normalized = (itemName || '').trim().toLowerCase();
      const joinedDecisions = nameToDecisions.get(normalized) || [];
      currentItems.push({
        name: r.Name,
        decision: r.Decision,
        url: r.url,
        notes: r.notes,
        description: r.description,
        allDecisions: joinedDecisions,
      });
    });
    if (currentItems.length) out.push({ topic: currentKey, items: currentItems });
    return out;
  }, [rows, nameToDecisions]);

  if (!groups.length) {
    return (
      <div className="p-4 border rounded bg-gray-50 text-sm text-gray-600">
        Commission streamlining decisions data unavailable.
      </div>
    );
  }

  const [openRows, setOpenRows] = useState(() => new Set());
  const toggleRow = (key) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-10 mb-6">
      {showDecisionLegend && (
        <div className="text-xs text-gray-600 italic">
          Legend: “Eliminate” indicates the Task Force proposes dissolving the body. Blank decisions
          are pending discussion.
        </div>
      )}
      {groups.map((g) => (
        <section key={g.topic} aria-labelledby={slugify(g.topic)}>
          <h4 id={slugify(g.topic)} className="text-lg font-semibold mb-3">
            {g.topic} Commissions
          </h4>
          <div className="overflow-x-auto not-prose">
            <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-2/3">Commission / Body</th>
                  <th className="text-left px-3 py-2 font-medium w-1/3">Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((item, i) => {
                  const rowKey = `${g.topic || 'unknown'}::${i}`;
                  const isOpen = openRows.has(rowKey);
                  const isExpandable = true;
                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        onClick={() => toggleRow(rowKey)}
                        className={`border-t border-gray-200 hover:bg-sky-50 transition-colors ${'cursor-pointer'}`}
                        role="button"
                        aria-expanded={isOpen ? 'true' : 'false'}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleRow(rowKey);
                          }
                        }}
                      >
                        <td className="px-3 py-0.5 align-top font-medium text-gray-800">
                          <div className="flex items-start gap-2">
                            <span
                              aria-hidden="true"
                              className={`mt-0.5 text-xs inline-block transition-transform text-gray-200 ${
                                isOpen ? 'rotate-90' : ''
                              }`}
                            >
                              ▶
                            </span>
                            <span>{item.name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-0.5 align-top">
                          {item.decision ? (
                            <DecisionBadge value={item.decision} />
                          ) : (
                            <span className="text-gray-400" title="Pending">
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="border-t border-gray-100">
                          <td className="px-3 py-2 text-gray-700 bg-sky-50/50" colSpan={2}>
                            {item.description ? (
                              <div className="mb-2 whitespace-pre-line">{item.description}</div>
                            ) : null}
                            {item.url ? (
                              <div className="mb-4">
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-brand-blue-5 hover:text-brand-blue-4 underline"
                                >
                                  Commission details
                                </a>
                              </div>
                            ) : null}
                            {item.allDecisions && item.allDecisions.length ? (
                              <div className="not-prose">
                                <div className="text-xs font-medium text-gray-800 mb-1.5">
                                  Task Force Decisions ({item.allDecisions.length})
                                </div>
                                <table className="w-full text-xs border border-gray-200 rounded">
                                  <thead className="bg-gray-100 text-gray-700">
                                    <tr>
                                      <th className="text-left px-2 py-1 w-60">Decision</th>
                                      <th className="text-left px-2 py-1">Comments</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item.allDecisions.map((d, di) => (
                                      <tr key={di} className="border-t border-gray-200">
                                        <td className="px-2 py-1 align-top font-medium text-gray-800">
                                          {d.decision || '—'}
                                        </td>
                                        <td className="px-2 py-1 align-top text-gray-700">
                                          {d.comments || '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}

function DecisionBadge({ value }) {
  const classes = {
    Eliminate: 'bg-brand-blue-3 text-white',
    Keep: 'bg-green-500 bg-opacity-20 text-green-900',
    Default: 'bg-gray-300 text-gray-800',
  };

  const cls = classes[value] || classes.Default;

  return (
    <span
      className={`inline-flex items-center text-[11px] leading-none px-2 py-1 rounded font-semibold tracking-wide ${cls}`}
    >
      {value}
    </span>
  );
}

function slugify(str) {
  return (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 60);
}
